import type {
  Annotation,
  Appointment,
  Communication,
  Organization,
  Practitioner,
  Reference,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes'
import { z } from 'zod'

// These need to match the values in the Knock workflow.
export const InboxItemType = {
  TaskAssigned: 'task-assigned',
  TaskNewComment: 'new-task-comment',
  NewProposedAppointment: 'new-proposed-appointment',
  NewCommentReply: 'new-comment-reply',
  CommentMention: 'comment-mention',
  NewComment: 'new-comment',
  TaskDue: 'task-due',
  FaxError: 'fax-error',
  // @deprecated see https://seenhealth.slack.com/archives/C082KPPHDLN/p1759416043926339?thread_ts=1759358095.438529&cid=C082KPPHDLN
  // retained for backwards compatibility
  OrderSigned: 'order-signed',
} as const
export type InboxItemType = (typeof InboxItemType)[keyof typeof InboxItemType]

const inboxItemSchema = z.nativeEnum(InboxItemType)

interface BaseInboxItem {
  type: z.infer<typeof inboxItemSchema>
}

export interface TaskAssignedInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.TaskAssigned
  actionUrl: string
  task: Reference<Task>
  /*
   * Flat string version of task.reference for Knock trigger_data filtering.
   * Knock removes nested structures from trigger_data filtering, so we need a flat scalar.
   * See: https://docs.knock.app/api-reference/overview/trigger-data-filtering
   */
  taskReferenceString: string
  owner: Reference<Practitioner>
  assigner?: Reference<Practitioner>
}

export interface TaskDueInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.TaskDue
  actionUrl: string
  task: Reference<Task>
  /**
   * Flat string version of task.reference for Knock trigger_data filtering.
   * Knock removes nested structures from trigger_data filtering, so we need a flat scalar.
   * See: https://docs.knock.app/api-reference/overview/trigger-data-filtering
   */
  taskReferenceString: string
}

export interface TaskNewCommentInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.TaskNewComment
  actionUrl: string
  task: Reference<Task>
  comment: Annotation
}

export interface NewProposedAppointmentInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.NewProposedAppointment
  actionUrl: string
  appointment: Reference<Appointment>
}

export interface NewCommentInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.NewComment
  comment: Reference<Communication>
  partOfResourceType?: string
  parentResource?: Reference
  // Root comment of the thread this notification belongs to. Present on
  // notifications sent after thread-root grouping shipped; absent on older ones
  // and whenever the thread root could not be proven, which fall back to
  // parentResource for grouping.
  threadRoot?: Reference<Communication>
}

export interface NewCommentReplyInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.NewCommentReply
  comment: Reference<Communication>
  partOfResourceType?: string
  parentResource?: Reference
  // Root comment of the thread this notification belongs to. Present on
  // notifications sent after thread-root grouping shipped; absent on older ones
  // and whenever the thread root could not be proven, which fall back to
  // parentResource for grouping.
  threadRoot?: Reference<Communication>
}

export interface CommentMentionInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.CommentMention
  comment: Reference<Communication>
  partOfResourceType?: string
  parentResource?: Reference
  // Root comment of the thread this notification belongs to. Present on
  // notifications sent after thread-root grouping shipped; absent on older ones
  // and whenever the thread root could not be proven, which fall back to
  // parentResource for grouping.
  threadRoot?: Reference<Communication>
}

// @deprecated see https://seenhealth.slack.com/archives/C082KPPHDLN/p1759416043926339?thread_ts=1759358095.438529&cid=C082KPPHDLN
export interface OrderSignedInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.OrderSigned
  actionUrl: string
  order: Reference<ServiceRequest>
  signedBy: Reference<Practitioner>
  requester: Reference<Practitioner>
}

export interface FaxErrorInboxItem extends BaseInboxItem {
  type: typeof InboxItemType.FaxError
  actionUrl: string
  communication: Reference<Communication>
  /*
   * Flat string version of communication.reference for Knock trigger_data filtering.
   * Knock removes nested structures from trigger_data filtering, so we need a flat scalar.
   * See: https://docs.knock.app/api-reference/overview/trigger-data-filtering
   */
  communicationReferenceString: string
  recipient: Reference<Practitioner | Organization>
  statusReason: string
}

export type InboxItem =
  | TaskAssignedInboxItem
  | TaskDueInboxItem
  | TaskNewCommentInboxItem
  | NewProposedAppointmentInboxItem
  | NewCommentInboxItem
  | NewCommentReplyInboxItem
  | CommentMentionInboxItem
  | FaxErrorInboxItem
  | OrderSignedInboxItem // @deprecated

export function isInboxItem(item: unknown): item is InboxItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    inboxItemSchema.safeParse(item.type).success
  )
}
